import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';

// @mui
import {
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// routes
import { PATH_DASHBOARD } from '../../routes/paths';

// hooks
import useSettings from '../../hooks/useSettings';
import useAuth from '../../hooks/useAuth';

// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';

// utils
import axiosInstance from '../../utils/axios';
import modifyScriptName from '../../utils/modifyScriptName';

// ----------------------------------------------------------------------

const SEGMENT_OPTIONS = [
  { value: 'NSE', label: 'NSE' },
  { value: 'MCX', label: 'MCX' },
];

/** Date only (no time), en-IN style dd/mm/yyyy */
const formatDateOnly = (value) => {
  if (value == null) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/** Highlighted row for start vs square-off dates */
function HighlightedDateRow({ label, dateStr, emphasis }) {
  const theme = useTheme();
  const paletteKey = emphasis === 'start' ? 'success' : 'warning';
  const main = theme.palette[paletteKey].main;
  const labelColor =
    emphasis === 'start' ? theme.palette.success.dark : theme.palette.warning.dark;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.25,
        py: 0.85,
        px: 1.25,
        borderRadius: 1,
        bgcolor: alpha(main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
        border: '1px solid',
        borderColor: alpha(main, 0.4),
        boxShadow: (t) => `inset 4px 0 0 0 ${t.palette[paletteKey].main}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: labelColor,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          fontSize: '0.95rem',
          color: 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {dateStr}
      </Typography>
    </Box>
  );
}

function ContractScheduleCell({ contract }) {
  if (!contract?.tradingsymbol) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  let displayName;
  try {
    displayName = modifyScriptName(contract.tradingsymbol, contract.expiry, contract);
  } catch {
    displayName = contract.tradingsymbol;
  }

  return (
    <Stack spacing={1} alignItems="stretch" sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
        {displayName}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <HighlightedDateRow
            label="Start date"
            dateStr={formatDateOnly(contract.enable_date)}
            emphasis="start"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <HighlightedDateRow
            label="Square off"
            dateStr={formatDateOnly(contract.squareoff_date)}
            emphasis="squareOff"
          />
        </Box>
      </Box>
    </Stack>
  );
}

function getReportsRootPath(rolename) {
  if (rolename === 'superAdmin') return PATH_DASHBOARD.report.root;
  if (rolename === 'admin') return PATH_DASHBOARD.insurerRole.report.root;
  return PATH_DASHBOARD.operatorRole.report.root;
}

// ----------------------------------------------------------------------

export default function ScriptsTimingReport() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [segments, setSegments] = useState(['NSE', 'MCX']);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState([]);
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 0,
  });

  const rolename = user?.role?.rolename || '';

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const exchangeParam = useMemo(() => {
    if (!segments.length) return undefined;
    return segments.join(',');
  }, [segments]);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (search) params.search = search;
      if (exchangeParam) params.exchange = exchangeParam;

      const res = await axiosInstance.get('/script/active-schedule', { params });
      const data = res.data?.data || {};
      setDocs(Array.isArray(data.docs) ? data.docs : []);
      const p = data.pagination || {};
      setPagination({
        totalDocs: p.totalDocs ?? 0,
        totalPages: p.totalPages ?? 0,
      });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to load script schedule', { variant: 'error' });
      setDocs([]);
      setPagination({ totalDocs: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, exchangeParam, enqueueSnackbar]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const breadcrumbLinks = [
    { name: 'Reports', href: getReportsRootPath(rolename) },
    { name: 'Scripts Timing' },
  ];

  return (
    <Page title="Scripts Timing">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs heading="Scripts Timing" links={breadcrumbLinks} />

        <Card sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <TextField
              label="Search script name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
              placeholder="e.g. NIFTY, GOLD"
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="scripts-timing-segment-label">Segments</InputLabel>
              <Select
                labelId="scripts-timing-segment-label"
                multiple
                value={segments}
                onChange={(e) => {
                  const v = e.target.value;
                  setSegments(typeof v === 'string' ? v.split(',') : v);
                  setPage(0);
                }}
                input={<OutlinedInput label="Segments" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => (
                      <Chip key={val} label={val} size="small" />
                    ))}
                  </Box>
                )}
              >
                {SEGMENT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={segments.indexOf(opt.value) > -1} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
              Clear all segments to include every exchange. Default is NSE and MCX.
            </Typography>
          </Box>
        </Card>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Script</TableCell>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Current active</TableCell>
                  <TableCell>Upcoming</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No rows match your filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  docs.map((row) => (
                    <TableRow key={`${row.exchange?._id || ''}-${row.name}`} hover>
                      <TableCell>
                        <Typography variant="subtitle2">{row.name}</Typography>
                      </TableCell>
                      <TableCell>{row.exchange?.name || '—'}</TableCell>
                      <TableCell sx={{ minWidth: 280, maxWidth: 420, verticalAlign: 'top' }}>
                        <ContractScheduleCell contract={row.currentActive} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 280, maxWidth: 480, verticalAlign: 'top' }}>
                        {row.upcoming?.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {row.upcoming.map((u) => (
                              <ContractScheduleCell key={u._id} contract={u} />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalDocs}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Card>
      </Container>
    </Page>
  );
}
